import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProfilePrivacySettings } from "@tabbio/types";
import { normalizeProfilePrivacySettings } from "@tabbio/utils/profile-privacy";
import {
  useProfile,
  useSaveProfilePrivacy,
} from "../../../hooks/useProfileApi";
import { useTailoredCVs } from "../../../hooks/useTailoredCvApi";
import {
  useCvShareLinks,
  useCvAccessGrants,
  useUpdateCvShareLink,
  useRevokeCvAccessGrant,
} from "../../../hooks/useCvCollaborationApi";
import { useAuth } from "../../../hooks/useAuth";
import { useToast } from "../../../hooks/useToast";
import { useSettingsDetail } from "../../../hooks/useSettingsDetail";
import { confirm } from "../../../lib/confirm-action";
import { trackProductEvent } from "../../../lib/product-analytics";
import {
  buildSettingsPrivacyInput,
  isSettingsAccessExpired,
} from "../../../lib/settings-privacy";
import { unpublishCv } from "../../../lib/settings-data-api";
import { invalidateCvDocumentQueries } from "../../../lib/cv-query-invalidation";
import {
  ControlStack,
  ControlCopy,
  ControlSection,
  ControlRow,
  ControlAction,
  ControlQueryState,
} from "./DataControlParts";
import { SettingsDetailSurface } from "./SettingsDetailSurface";
import { ShareLinkDetails } from "../../cv/ShareLinkDetails";

type Detail =
  | { kind: "audience" | "contact" | "sharing" }
  | { kind: "cv"; id: string; title: string };
export function CvPrivacyControls() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const profile = useProfile(!!user, "master");
  const cvs = useTailoredCVs(!!user);
  const save = useSaveProfilePrivacy();
  const unavailable =
    profile.isError || (profile.isSuccess && !profile.data?.privacy);
  const toast = useToast();
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const surface = useSettingsDetail<Detail>({ kind: "audience" });
  const privacy = normalizeProfilePrivacySettings(profile.data?.privacy);
  const change = async (patch: Partial<ProfilePrivacySettings>) => {
    if (lock.current || !profile.data?.privacy || unavailable) return;
    lock.current = true;
    setBusy(true);
    try {
      const publishing =
        patch.profileVisibility === "public" &&
        privacy.profileVisibility !== "public";
      if (
        publishing &&
        !(await confirm({
          title: t("settings.cvPrivacy.publishTitle"),
          message: t("settings.cvPrivacy.publishBody"),
          confirmLabel: t("settings.cvPrivacy.publishAction"),
          cancelLabel: t("actions.cancel"),
        }))
      )
        return;
      await save.mutateAsync(
        buildSettingsPrivacyInput(
          privacy,
          patch,
          publishing || privacy.profileVisibility === "public",
        ),
      );
      await profile.refetch();
      toast.success(t("settings.privacy.saved"));
      if (publishing) {
        trackProductEvent("profile_published", { source: "settings" });
      } else if (patch.profileVisibility) {
        trackProductEvent("profile_visibility_changed", {
          visibility: patch.profileVisibility,
        });
      }
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const option = (
    value: string,
    current: string,
    onPress: () => void,
    description?: string,
  ) => (
    <ControlRow
      key={value}
      title={t(`settings.cvPrivacy.visibility.${value}`)}
      description={description}
      selected={value === current}
      disabled={busy || unavailable}
      onPress={onPress}
    />
  );
  const detailTitle =
    surface.detail.kind === "cv"
      ? surface.detail.title
      : t(`settings.controls.${surface.detail.kind}`);
  const cvId = surface.detail.kind === "cv" ? surface.detail.id : null;
  const selectedCv = cvs.data?.find((cv) => cv.id === cvId);
  return (
    <>
      <ControlStack gap={24}>
        <ControlSection title={t("settings.cvPrivacy.mainCv")}>
          <ControlQueryState
            loading={profile.isPending}
            error={unavailable}
            retry={profile.refetch}
          />
          {profile.data?.privacy && !unavailable ? (
            <>
              <ControlRow
                icon="privacy"
                title={t("settings.controls.audience")}
                description={t("settings.controls.audienceHint")}
                value={t(
                  `settings.cvPrivacy.visibility.${privacy.profileVisibility}`,
                )}
                onPress={() => surface.open({ kind: "audience" })}
              />
              <ControlRow
                icon="contact"
                title={t("settings.controls.contact")}
                description={t("settings.controls.contactHint")}
                onPress={() => surface.open({ kind: "contact" })}
              />
              <ControlRow
                icon="link"
                title={t("settings.controls.sharing")}
                description={t("settings.controls.sharingHint")}
                onPress={() => surface.open({ kind: "sharing" })}
              />
            </>
          ) : null}
        </ControlSection>
        <ControlSection title={t("settings.cvPrivacy.otherCvs")}>
          <ControlQueryState
            loading={cvs.isPending}
            error={cvs.isError}
            retry={cvs.refetch}
          />
          {cvs.isSuccess && !cvs.data.length ? (
            <ControlCopy>{t("settings.cvPrivacy.noCvs")}</ControlCopy>
          ) : null}
          {cvs.isSuccess
            ? cvs.data.map((cv) => (
                <ControlRow
                  key={cv.id}
                  icon="cv"
                  title={cv.title}
                  description={t(
                    cv.isPublic
                      ? "settings.controls.publicPageOn"
                      : "settings.controls.publicPageOff",
                  )}
                  onPress={() =>
                    surface.open({ kind: "cv", id: cv.id, title: cv.title })
                  }
                />
              ))
            : null}
        </ControlSection>
        <ControlCopy>{t("settings.cvPrivacy.independentAccess")}</ControlCopy>
      </ControlStack>
      <SettingsDetailSurface
        visible={surface.visible}
        title={detailTitle}
        onClose={surface.close}
        onAfterClose={surface.afterClose}
      >
        {surface.detail.kind === "audience" ? (
          <>
            <ControlCopy>{t("settings.controls.audienceHint")}</ControlCopy>
            <ControlStack gap={0}>
              {(["public", "employers", "private"] as const).map((value) =>
                option(
                  value,
                  privacy.profileVisibility,
                  () => void change({ profileVisibility: value }),
                  t(`settings.controls.audienceOptions.${value}`),
                ),
              )}
            </ControlStack>
          </>
        ) : surface.detail.kind === "contact" ? (
          <>
            <ControlCopy>{t("settings.controls.contactHint")}</ControlCopy>
            <ControlSection title={t("settings.cvPrivacy.photo")}>
              {(["public", "employers", "hidden"] as const).map((value) =>
                option(
                  value,
                  privacy.photoVisibility,
                  () => void change({ photoVisibility: value }),
                ),
              )}
            </ControlSection>
            <ControlSection title={t("settings.email")}>
              {(["employers", "hidden"] as const).map((value) =>
                option(
                  value,
                  privacy.contactVisibility.email,
                  () =>
                    void change({
                      contactVisibility: {
                        ...privacy.contactVisibility,
                        email: value,
                      },
                    }),
                ),
              )}
            </ControlSection>
            <ControlSection title={t("settings.cvPrivacy.phone")}>
              {(["employers", "hidden"] as const).map((value) =>
                option(
                  value,
                  privacy.contactVisibility.phone,
                  () =>
                    void change({
                      contactVisibility: {
                        ...privacy.contactVisibility,
                        phone: value,
                      },
                    }),
                ),
              )}
            </ControlSection>
          </>
        ) : surface.detail.kind === "sharing" ? (
          surface.visible ? (
            <SharingAccess target="master" />
          ) : null
        ) : selectedCv ? (
          <>
            <TailoredPublicControl cv={selectedCv} />
            {surface.visible ? <SharingAccess target={selectedCv.id} /> : null}
          </>
        ) : (
          <ControlQueryState
            loading={cvs.isPending}
            error={cvs.isError}
            retry={cvs.refetch}
          />
        )}
      </SettingsDetailSurface>
    </>
  );
}

function TailoredPublicControl({
  cv,
}: {
  cv: { id: string; title: string; isPublic: boolean };
}) {
  const { t } = useTranslation("common");
  const toast = useToast();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => unpublishCv(cv.id),
    onSuccess: () => invalidateCvDocumentQueries(queryClient, cv.id),
  });
  const lock = useRef(false);
  const turnOff = async () => {
    if (lock.current) return;
    lock.current = true;
    try {
      if (
        !(await confirm({
          title: t("settings.cvPrivacy.unpublishTitle"),
          message: t("settings.cvPrivacy.unpublishBody", { title: cv.title }),
          confirmLabel: t("settings.cvPrivacy.unpublish"),
          cancelLabel: t("actions.cancel"),
        }))
      )
        return;
      await mutation.mutateAsync();
      toast.success(t("settings.privacy.saved"));
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      lock.current = false;
    }
  };
  return (
    <ControlStack gap={12}>
      <ControlCopy title>
        {t(
          cv.isPublic
            ? "settings.controls.publicPageOn"
            : "settings.controls.publicPageOff",
        )}
      </ControlCopy>
      <ControlCopy>{t("settings.controls.cvSharingHint")}</ControlCopy>
      {cv.isPublic ? (
        <ControlAction
          title={t("settings.cvPrivacy.unpublish")}
          disabled={mutation.isPending}
          onPress={() => void turnOff()}
        />
      ) : null}
    </ControlStack>
  );
}

function SharingAccess({ target }: { target: string }) {
  const { t } = useTranslation("common");
  const toast = useToast();
  const links = useCvShareLinks(target);
  const grants = useCvAccessGrants(target);
  const updateLink = useUpdateCvShareLink(target);
  const revokeGrant = useRevokeCvAccessGrant(target);
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const revoke = async (id: string, kind: "link" | "grant") => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      if (
        !(await confirm({
          title: t("settings.cvPrivacy.revokeTitle"),
          message: t("settings.cvPrivacy.revokeBody"),
          confirmLabel: t("settings.cvPrivacy.revoke"),
          cancelLabel: t("actions.cancel"),
          destructive: true,
        }))
      )
        return;
      if (kind === "link")
        await updateLink.mutateAsync({ shareLinkId: id, revoked: true });
      else await revokeGrant.mutateAsync(id);
      await Promise.all([links.refetch(), grants.refetch()]);
      toast.success(t("settings.privacy.saved"));
    } catch {
      toast.error(t("settings.privacy.updateFailed"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const visibleGrants =
    grants.data?.filter(
      (grant) => grant.status === "active" || grant.status === "pending",
    ) ?? [];
  return (
    <ControlStack gap={24}>
      <ControlSection title={t("settings.cvPrivacy.shareLinks")}>
        <ControlQueryState
          loading={links.isPending}
          error={links.isError}
          retry={links.refetch}
        />
        {links.isSuccess && !links.data.length ? (
          <ControlCopy>{t("settings.cvPrivacy.noLinks")}</ControlCopy>
        ) : null}
        {links.isSuccess
          ? links.data.map((link) => (
              <ControlStack key={link.id} gap={8}>
                <ControlRow
                  title={
                    link.label ||
                    link.tokenPreview ||
                    t("settings.cvPrivacy.shareLink")
                  }
                  description={`${t(isSettingsAccessExpired(link.expiresAt) ? "settings.cvPrivacy.expired" : "settings.cvPrivacy.anyoneWithLink")} · ${t(link.allowDownload ? "settings.cvPrivacy.downloadsOn" : "settings.cvPrivacy.downloadsOff")}`}
                />
                {/* What this link shares and under which policy (E03). The
                    CV is the one this section is about, so it is not repeated. */}
                <ShareLinkDetails link={link} cv={null} />
                <ControlAction
                  title={t("settings.cvPrivacy.revoke")}
                  disabled={busy}
                  onPress={() => void revoke(link.id, "link")}
                />
              </ControlStack>
            ))
          : null}
      </ControlSection>
      <ControlSection title={t("settings.cvPrivacy.people")}>
        <ControlQueryState
          loading={grants.isPending}
          error={grants.isError}
          retry={grants.refetch}
        />
        {grants.isSuccess && !visibleGrants.length ? (
          <ControlCopy>{t("settings.cvPrivacy.noPeople")}</ControlCopy>
        ) : null}
        {grants.isSuccess
          ? visibleGrants.map((grant) => (
              <ControlStack key={grant.id} gap={8}>
                <ControlRow
                  title={
                    grant.granteeEmail || t("settings.cvPrivacy.sharedAccess")
                  }
                  description={t(
                    isSettingsAccessExpired(grant.expiresAt)
                      ? "settings.cvPrivacy.expired"
                      : grant.status === "pending"
                        ? "settings.cvPrivacy.pending"
                        : "settings.cvPrivacy.active",
                  )}
                />
                <ControlAction
                  title={t("settings.cvPrivacy.revoke")}
                  disabled={busy}
                  onPress={() => void revoke(grant.id, "grant")}
                />
              </ControlStack>
            ))
          : null}
      </ControlSection>
    </ControlStack>
  );
}
